import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import CookieBanner from "./components/CookieBanner";

// Home stays eagerly imported because it's the marketing landing page —
// every cold visit pays for it anyway, and avoiding the Suspense flash on
// the LCP-critical route is worth the ~30 KB. All other pages are split
// into their own chunks so the initial bundle stays lean (~70% smaller
// for a first-time visitor who never clicks past the home page).
import Home from "./pages/Home";

const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const Settings = lazy(() => import("./pages/Settings"));
const Analytics = lazy(() => import("./pages/Analytics"));
const FollowupsManager = lazy(() => import("./pages/FollowupsManager"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MagicLogin = lazy(() => import("./pages/MagicLogin"));
const RestoreAccount = lazy(() => import("./pages/RestoreAccount"));
const ViewQuote = lazy(() => import("./pages/ViewQuote"));
const ClientApprovalPortal = lazy(() => import("./pages/ClientApprovalPortal"));
const AIEstimatePage = lazy(() => import("./pages/AIEstimatePage"));
const Team = lazy(() => import("./pages/Team"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const ContractorProfile = lazy(() => import("./pages/ContractorProfile"));
const Developers = lazy(() => import("./pages/Developers"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const InvoicePortal = lazy(() => import("./pages/InvoicePortal"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Reviews = lazy(() => import("./pages/Reviews"));
const ReviewPortal = lazy(() => import("./pages/ReviewPortal"));
const Privacy = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Terms })));
const DeleteAccountRequest = lazy(() => import("./pages/Legal").then((m) => ({ default: m.DeleteAccountRequest })));

// Tiny shared fallback — matches the dashboard skeleton tone so the
// transition feels intentional rather than a content flash.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">
    Loading…
  </div>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/followups" element={<FollowupsManager />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/magic-login" element={<MagicLogin />} />
              <Route path="/admin/platform" element={<AdminDashboard />} />
              <Route path="/restore-account" element={<RestoreAccount />} />
              <Route path="/view-quote/:quoteId" element={<ViewQuote />} />
              <Route path="/portal/:portalToken" element={<ClientApprovalPortal />} />
              <Route path="/ai-estimate" element={<AIEstimatePage />} />
              <Route path="/team" element={<Team />} />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/contractors/:slug" element={<ContractorProfile />} />
              <Route path="/developers" element={<Developers />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
              <Route path="/pay/:token" element={<InvoicePortal />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:jobId" element={<JobDetail />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/review/:token" element={<ReviewPortal />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/delete-account-request" element={<DeleteAccountRequest />} />
            </Routes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
        <Toaster />
        <InstallPrompt />
      </div>
    </AuthProvider>
  );
}

export default App;
